<template>
  <div class="container mt-2">
    <!-- Header -->
    <div class="hero is-dark">
      <div class="hero-body">
        <div class="container">
          <h1 class="title is-4">
            <span class="icon">
              <i class="fa fa-upload"></i>
            </span>
            {{ $t("upload required documents") }}
          </h1>
          <p class="subtitle is-6">
            {{
              $t(
                "please upload all required documents to complete your application"
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="error" class="notification is-danger" v-html="error"></div>

    <!-- Progress indicator -->
    <div class="box" v-if="documentation.length > 0">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <p class="heading">{{ $t("progress") }}</p>
              <p class="title is-6">
                {{ uploadedDocuments.length }}/{{ requiredDocuments.length }}
                {{ $t("documents uploaded") }}
              </p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <progress
              class="progress is-primary"
              :value="uploadedDocuments.length"
              :max="requiredDocuments.length"
            >
              {{
                Math.round(
                  (uploadedDocuments.length / requiredDocuments.length) * 100
                )
              }}%
            </progress>
          </div>
        </div>
      </div>
    </div>

    <!-- Document upload sections -->
    <div v-if="documentation.length > 0" class="columns is-multiline">
      <div
        v-for="(doc, index) in documentation"
        :key="index"
        class="column is-full-tablet is-half-desktop"
      >
        <div
          class="card"
          :class="{
            'has-border-success': isDocumentUploaded(doc),
            'has-border-danger': doc.is_required && !isDocumentUploaded(doc),
          }"
        >
          <div class="card-header">
            <div class="card-header-title">
              <span
                class="icon"
                :class="{
                  'has-text-success': isDocumentUploaded(doc),
                  'has-text-danger':
                    doc.is_required && !isDocumentUploaded(doc),
                }"
              >
                <i
                  class="fa"
                  :class="
                    isDocumentUploaded(doc)
                      ? 'fa-check-circle'
                      : doc.is_required
                      ? 'fa-exclamation-circle'
                      : 'fa-circle-o'
                  "
                ></i>
              </span>
              <span>{{ doc.name }}</span>
              <span
                v-if="doc.is_required"
                class="tag is-danger is-small ml-2"
                >{{ $t("required") }}</span
              >
            </div>
          </div>

          <div class="card-content">
            <!-- Upload area -->
            <div
              class="file-drop-area"
              :class="{
                'is-active': dragStates[index],
                'has-file': getUploadedFile(doc),
                'is-uploading': uploadStates[index],
              }"
              @dragover.prevent="onDragOver(index)"
              @dragleave.prevent="onDragLeave(index)"
              @drop.prevent="onDrop(index, $event, doc)"
              @click="triggerFileInput(index)"
            >
              <input
                :ref="`fileInput${index}`"
                type="file"
                accept="image/*,.pdf"
                style="display: none"
                @change="onFileSelect(index, $event, doc)"
              />

              <!-- Upload content -->
              <div
                v-if="!getUploadedFile(doc) && !uploadStates[index]"
                class="upload-content"
              >
                <div class="upload-icon">
                  <i class="fa fa-cloud-upload fa-3x"></i>
                </div>
                <p class="upload-text">
                  <strong>{{ $t("click to upload") }}</strong>
                  {{ $t("or drag and drop") }}
                </p>
                <p class="upload-help">
                  {{ $t("accepted formats") }}: JPG, PNG, PDF ({{
                    $t("max size")
                  }}: 10MB)
                </p>
              </div>

              <!-- Uploading state -->
              <div v-if="uploadStates[index]" class="upload-content">
                <div class="upload-icon">
                  <i class="fa fa-spinner fa-spin fa-3x"></i>
                </div>
                <p class="upload-text">
                  <strong>{{ $t("uploading") }}...</strong>
                </p>
                <progress
                  v-if="uploadProgress[index] !== undefined"
                  class="progress is-primary is-small mt-2"
                  :value="uploadProgress[index]"
                  max="100"
                >
                  {{ uploadProgress[index] }}%
                </progress>
              </div>

              <!-- Uploaded file display -->
              <div
                v-if="getUploadedFile(doc) && !uploadStates[index]"
                class="uploaded-file"
              >
                <div class="file-preview">
                  <div class="file-icon">
                    <i
                      class="fa"
                      :class="getFileIcon(getUploadedFile(doc))"
                    ></i>
                  </div>
                  <div class="file-info">
                    <p class="file-name">{{ getUploadedFile(doc).name }}</p>
                    <p class="file-size">
                      {{ formatFileSize(getUploadedFile(doc).size) }}
                    </p>
                    <p class="file-date">
                      {{ $t("uploaded") }}:
                      {{ formatUploadDate(getUploadedFile(doc).uploaded_at) }}
                    </p>
                  </div>
                </div>

                <!-- File actions moved below -->
                <div class="file-actions mt-3">
                  <button
                    class="button is-small is-info"
                    @click.stop="previewFile(getUploadedFile(doc))"
                    :title="$t('preview')"
                  >
                    <span class="icon">
                      <i class="fa fa-eye"></i>
                    </span>
                    <span>{{ $t("preview") }}</span>
                  </button>
                  <button
                    class="button is-small is-danger"
                    @click.stop="removeFile(doc, index)"
                    :title="$t('remove')"
                  >
                    <span class="icon">
                      <i class="fa fa-trash"></i>
                    </span>
                    <span>{{ $t("remove") }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- File requirements -->
            <div class="file-requirements mt-3">
              <p class="is-size-7 has-text-grey">
                <span class="icon is-small">
                  <i class="fa fa-info-circle"></i>
                </span>
                {{ getDocumentRequirements(doc) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit section -->
    <div v-if="documentation.length > 0" class="box mt-5">
      <div class="field">
        <div class="control">
          <button
            class="button is-success is-large is-fullwidth"
            :class="{ 'is-loading': submitting }"
            :disabled="!canSubmit || submitting"
            @click="submitDocuments"
          >
            <span class="icon">
              <i class="fa fa-check"></i>
            </span>
            <span>{{ $t("submit documents") }}</span>
          </button>
        </div>
      </div>

      <div v-if="!canSubmit" class="notification is-warning">
        <p>
          {{ $t("please upload all required documents before submitting") }}
        </p>
        <ul class="mt-2">
          <li v-for="doc in missingRequiredDocuments" :key="doc.name">
            • {{ doc.name }}
          </li>
        </ul>
      </div>
    </div>

    <!-- File preview modal -->
    <div class="modal" :class="{ 'is-active': showPreviewModal }">
      <div class="modal-background" @click="closePreviewModal"></div>
      <div class="modal-content">
        <div class="box">
          <div v-if="currentPreviewFile" class="preview-container">
            <h4 class="title is-5">{{ currentPreviewFile.name }}</h4>
            <div v-if="isImageFile(currentPreviewFile)" class="image-preview">
              <img
                :src="currentPreviewFile.url"
                :alt="currentPreviewFile.name"
              />
            </div>
            <div v-else class="pdf-preview">
              <iframe
                :src="currentPreviewFile.url"
                width="100%"
                height="500px"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
      <button class="modal-close is-large" @click="closePreviewModal"></button>
    </div>
  </div>
</template>

<script>
import {
  warning_message,
  get,
  generate_error_message_text,
  post,
  format_date,
  success_message,
  drive_static_file_url,
} from "@/utils";
import moment from "moment";

export default {
  name: "PersonalSavingsAccountRequestDocumentation",
  components: {},
  data() {
    return {
      error: "",
      documentation: [],
      uploadedFiles: {},
      dragStates: {},
      uploadStates: {},
      uploadProgress: {},
      submitting: false,
      showPreviewModal: false,
      currentPreviewFile: null,
      uuid: null,
      request: null,
    };
  },
  computed: {
    requiredDocuments() {
      return this.documentation.filter((doc) => doc.is_required);
    },
    uploadedDocuments() {
      return this.documentation.filter((doc) => this.isDocumentUploaded(doc));
    },
    missingRequiredDocuments() {
      return this.requiredDocuments.filter(
        (doc) => !this.isDocumentUploaded(doc)
      );
    },
    canSubmit() {
      return this.missingRequiredDocuments.length === 0;
    },
  },
  methods: {
    format_date,
    drive_static_file_url,

    load_request(uuid) {
      let v = this;
      v.uuid = uuid;
      v.$root.loader(true);
      get("/account-request/personal-savings/complete/documentation", {
        uuid: uuid,
      })
        .then((response) => {
          let { flow, status, documentation } = response;
          v.request = response;
          if (!documentation.length) {
            if (flow === "capture-documentation-verification") {
              v.$router.push({
                path: `/account-request/personal-savings/verification/${uuid}`,
              });
            } else if (flow === "capture-verification-documentation") {
              v.$router.push({
                path: `/account-request/personal-savings/process-finished`,
              });
            } else {
              v.error = v.$t("flow not recognized");
              console.error("Unknown flow", flow);
            }
          } else {
            if (status === "documentation-completed") {
              if (flow === "capture-documentation-verification") {
                v.$router.push({
                  path: `/account-request/personal-savings/verification/${uuid}`,
                });
              } else if (flow === "capture-verification-documentation") {
                v.$router.push({
                  path: `/account-request/personal-savings/process-finished`,
                });
              } else {
                v.error = v.$t("flow not recognized");
                console.error("Unknown flow", flow);
              }
            }
            v.documentation = documentation;
            v.initializeStates();
          }
        })
        .catch((error) => {
          console.error(error);
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },

    initializeStates() {
      // Initialize reactive states for each document
      const dragStates = {};
      const uploadStates = {};
      const uploadProgress = {};

      this.documentation.forEach((doc, index) => {
        dragStates[index] = false;
        uploadStates[index] = false;
        uploadProgress[index] = 0;
      });

      this.dragStates = dragStates;
      this.uploadStates = uploadStates;
      this.uploadProgress = uploadProgress;
    },

    // File upload methods
    triggerFileInput(index) {
      if (this.uploadStates[index]) return;
      const fileInput = this.$refs[`fileInput${index}`][0];
      if (fileInput) {
        fileInput.click();
      }
    },

    onFileSelect(index, event, doc) {
      const file = event.target.files[0];
      if (file) {
        this.uploadFile(index, file, doc);
      }
    },

    onDragOver(index) {
      this.dragStates[index] = true;
    },

    onDragLeave(index) {
      this.dragStates[index] = false;
    },

    onDrop(index, event, doc) {
      this.dragStates[index] = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadFile(index, file, doc);
      }
    },

    async uploadFile(index, file, doc) {
      // Validate file
      if (!this.validateFile(file)) {
        return;
      }

      this.uploadStates[index] = true;
      this.uploadProgress[index] = 0;

      try {
        // Store the actual file for later submission
        const uploadedFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
          uploaded_at: new Date().toISOString(),
          document_type: doc.name,
          file: file, // Store the actual File object
        };

        // Simulate upload progress for UI feedback
        const simulateProgress = () => {
          return new Promise((resolve) => {
            let progress = 0;
            const interval = setInterval(() => {
              progress += Math.random() * 30;
              if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
                resolve();
              }
              this.uploadProgress[index] = Math.round(progress);
            }, 200);
          });
        };

        await simulateProgress();
        this.uploadedFiles[doc.name] = uploadedFile;
      } catch (error) {
        console.error("Upload error:", error);
        this.error = this.$t("error uploading file");
      } finally {
        this.uploadStates[index] = false;
      }
    },

    validateFile(file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];

      if (file.size > maxSize) {
        this.error = this.$t("file too large maximum size is 10mb");
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        this.error = this.$t(
          "invalid file type only jpg png and pdf are allowed"
        );
        return false;
      }

      this.error = "";
      return true;
    },

    // Document state methods
    isDocumentUploaded(doc) {
      return !!this.uploadedFiles[doc.name];
    },

    getUploadedFile(doc) {
      return this.uploadedFiles[doc.name];
    },

    removeFile(doc, index) {
      if (confirm(this.$t("are you sure you want to remove this file"))) {
        delete this.uploadedFiles[doc.name];
        // Reset file input
        const fileInput = this.$refs[`fileInput${index}`][0];
        if (fileInput) {
          fileInput.value = "";
        }
      }
    },

    // File display methods
    getFileIcon(file) {
      if (file.type.startsWith("image/")) {
        return "fa-file-image-o";
      } else if (file.type === "application/pdf") {
        return "fa-file-pdf-o";
      }
      return "fa-file-o";
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    formatUploadDate(dateString) {
      return moment(dateString).format("DD/MM/YYYY HH:mm");
    },

    getDocumentRequirements(doc) {
      const requirements = {
        "Frente DPI": this.$t("clear photo of the front of your dpi document"),
        "Trasera DPI": this.$t("clear photo of the back of your dpi document"),
        Selfie: this.$t("clear selfie photo holding your dpi document"),
      };
      return (
        requirements[doc.name] ||
        this.$t("upload a clear photo or pdf of this document")
      );
    },

    // Preview methods
    previewFile(file) {
      this.currentPreviewFile = file;
      this.showPreviewModal = true;
    },

    closePreviewModal() {
      this.showPreviewModal = false;
      this.currentPreviewFile = null;
    },

    isImageFile(file) {
      return file.type.startsWith("image/");
    },

    // Submit method
    async submitDocuments() {
      if (!this.canSubmit) return;

      this.submitting = true;
      try {
        // Create FormData to send files
        const formData = new FormData();

        // Add UUID
        formData.append("uuid", this.uuid);

        // Add each uploaded file with its document type
        Object.values(this.uploadedFiles).forEach((uploadedFile, index) => {
          formData.append(uploadedFile.document_type, uploadedFile.file);
          // formData.append(
          //   `document_types[${index}]`,
          //   uploadedFile.document_type
          // );
          // formData.append(`file_names[${index}]`, uploadedFile.name);
        });
        // formData.append(
        //   "document_types",
        //   JSON.stringify(
        //     Object.values(this.uploadedFiles).map((f) => f.document_type)
        //   )
        // );

        // Send FormData with files
        // Try using the utils post method first, fallback to axios if needed
        await post(
          "/account-request/personal-savings/complete/documentation",
          formData
        );

        let flow = this.request.flow;
        if (flow === "capture-documentation-verification") {
          this.$router.push({
            path: `/account-request/personal-savings/verification/${this.request.uuid}`,
          });
        } else if (flow === "capture-verification-documentation") {
          this.$router.push({
            path: `/account-request/personal-savings/process-finished`,
          });
        } else {
          console.error("Unknown flow", flow);
        }
      } catch (error) {
        console.error("Submit error:", error);
        this.error =
          generate_error_message_text(error) ||
          this.$t("error submitting documents");
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    let uuid = this.$route.params.uuid;
    if (uuid) {
      this.load_request(uuid);
    } else {
      this.error = this.$t("invalid request id");
    }
  },
};
</script>

<style scoped>
/* Card borders for document status */
.card.has-border-success {
  border-left: 4px solid #48c774;
}

.card.has-border-danger {
  border-left: 4px solid #e75757;
}

/* File drop area styling */
.file-drop-area {
  border: 2px dashed #dbdbdb;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.file-drop-area:hover {
  border-color: #3273dc;
  background: #f0f8ff;
}

.file-drop-area.is-active {
  border-color: #48c774;
  background: #f0fff4;
  transform: scale(1.02);
}

.file-drop-area.has-file {
  border-color: #48c774;
  background: #f0fff4;
}

.file-drop-area.is-uploading {
  border-color: #3273dc;
  background: #f0f8ff;
  cursor: not-allowed;
}

/* Upload content styling */
.upload-content {
  text-align: center;
}

.upload-icon {
  color: #b5b5b5;
  margin-bottom: 1rem;
}

.upload-text {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.upload-text strong {
  color: #a5a5a5;
}

.upload-help {
  font-size: 0.9rem;
  color: #7a7a7a;
}

/* Uploaded file display */
.uploaded-file {
  display: block;
  width: 100%;
  text-align: left;
}

.file-preview {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.file-icon {
  font-size: 2rem;
  color: #48c774;
  margin-right: 1rem;
}

.file-info {
  flex: 1;
}

.file-name {
  font-weight: bold;
  margin-bottom: 0.25rem;
  word-break: break-word;
}

.file-size,
.file-date {
  font-size: 0.85rem;
  color: #7a7a7a;
  margin-bottom: 0.25rem;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  padding-top: 0.75rem;
  border-top: 1px solid #ededed;
}

/* File requirements */
.file-requirements {
  border-top: 1px solid #ededed;
  padding-top: 0.75rem;
}

/* Progress styling */
.progress {
  margin-bottom: 0;
}

/* Preview modal */
.preview-container {
  max-width: 100%;
}

.image-preview img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.pdf-preview iframe {
  border: none;
  border-radius: 4px;
}

/* Mobile responsiveness */
@media screen and (max-width: 768px) {
  /* Container and hero adjustments */
  .container.mt-2 {
    margin-top: 0.5rem !important;
  }

  .hero-body {
    padding: 1.5rem 1rem;
  }

  .title.is-4 {
    font-size: 1.25rem !important;
  }

  .subtitle.is-6 {
    font-size: 0.9rem !important;
  }

  /* Progress box adjustments */
  .box {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  /* Card adjustments */
  .card {
    margin-bottom: 1rem;
  }

  .card-header {
    padding: 0.75rem 1rem;
  }

  .card-header-title {
    font-size: 0.95rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .card-content {
    padding: 1rem;
  }

  /* File drop area - much more compact */
  .file-drop-area {
    padding: 1rem 0.75rem;
    min-height: 120px;
    border-radius: 6px;
  }

  .upload-icon {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }

  .upload-text {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }

  .upload-help {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  /* Uploaded file display - more compact */
  .file-preview {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
    margin-bottom: 0.75rem;
  }

  .file-icon {
    font-size: 1.5rem;
    margin-right: 0.75rem;
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .file-info {
    flex: 1;
    min-width: 0;
  }

  .file-name {
    font-size: 0.9rem;
    margin-bottom: 0.2rem;
  }

  .file-size,
  .file-date {
    font-size: 0.75rem;
    margin-bottom: 0.15rem;
  }

  /* File actions - horizontal layout on mobile */
  .file-actions {
    flex-direction: row;
    gap: 0.5rem;
    justify-content: space-between;
    padding-top: 0.5rem;
  }

  .file-actions .button {
    flex: 1;
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
  }

  .file-actions .button .icon {
    font-size: 0.75rem;
  }

  /* Column adjustments */
  .column.is-full-tablet.is-half-desktop {
    padding: 0.25rem;
  }

  /* File requirements */
  .file-requirements {
    padding-top: 0.5rem;
    margin-top: 0.5rem;
  }

  .file-requirements p {
    font-size: 0.75rem;
  }

  /* Progress bar adjustments */
  .level {
    margin-bottom: 0.5rem;
  }

  .level-item {
    margin-bottom: 0.25rem;
  }

  .progress {
    width: 100%;
    margin-top: 0.5rem;
  }

  /* Submit section */
  .mt-5 {
    margin-top: 1.5rem !important;
  }

  .button.is-large {
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }

  /* Modal adjustments */
  .modal-content {
    margin: 1rem;
    max-width: calc(100vw - 2rem);
  }

  .modal-content .box {
    padding: 1rem;
  }

  .preview-container .title.is-5 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .pdf-preview iframe {
    height: 400px;
  }

  /* Notification adjustments */
  .notification {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .notification ul {
    margin-left: 0.5rem;
  }

  .notification li {
    font-size: 0.9rem;
  }

  /* Tag adjustments */
  .tag.is-small {
    font-size: 0.65rem;
  }
}

/* Animation for drag states */
.file-drop-area {
  transition: all 0.2s ease-in-out;
}

/* Loading spinner for upload */
.fa-spinner.fa-spin {
  color: #3273dc;
}

/* Success states */
.has-text-success {
  color: #48c774 !important;
}

.has-text-danger {
  color: #f14668 !important;
}

/* Custom progress bar */
.progress.is-small {
  height: 0.5rem;
}

/* Notification styling */
.notification.is-warning ul {
  margin-left: 1rem;
}

.notification.is-warning li {
  margin-bottom: 0.25rem;
}
</style>
